import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import { useBedbankPackageViewsForIndividualRoom } from 'hooks/useBedbankPackageViews'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Pane from 'components/Common/Pane'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { capitalise } from 'lib/string/stringUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import BedbankIndividualRoomSelectRoom from '../MultiRoom/BedbankIndividualRoomSelectRoom'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getRemainingAvailabilityByPackageAndSupplier, getRemainingOverallAvailabilityByPackage } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import ModalBase from 'LuxKit/Modal/ModalBase'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { EmptyObject } from 'lib/object/objectUtils'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'

interface Props {
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
  roomId: string
  remainingAvailabilityByPackageAndSupplier: Record<string, Record<string, number>>
  remainingOverallAvailabilityByPackage: Record<string, number>
}

function BedbankIndividualRoomPackageSelectionModal(props: Props) {
  const {
    offer,
    checkInDate,
    checkOutDate,
    rooms,
    roomId,
    remainingAvailabilityByPackageAndSupplier,
    remainingOverallAvailabilityByPackage,
  } = props

  const modalElement = useModalElementContext()

  const roomIndex = rooms.findIndex(r => r.id === roomId)

  const room = rooms[roomIndex]

  const initialPackageId = useRef(room.roomId).current

  const dispatch = useOfferPageDispatch()
  const [packageViews] = useBedbankPackageViewsForIndividualRoom(offer, room)
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)

  const hasPromotions = offer.promotions.length > 0
  const showAll = packageViews.filter(p => p.available).length === 0

  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.package.roomId === initialPackageId) {
        return Number.MIN_SAFE_INTEGER
      }
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER + 1
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews, validCheckinCheckoutDates, hasPromotions, initialPackageId])

  const displayablePackages = useMemo(() => sortedPackageViews.filter(
    packageView => packageView.show && (packageView.available || showAll),
  ), [sortedPackageViews, showAll])

  const close = useCallback(() => { modalElement.resolve() }, [modalElement])

  const onOptionSelect = useCallback((packageOption: App.BedbankPackage, rate?: App.BedbankRate) => {
    if (packageOption) {
      dispatch({
        type: OfferPageActions.selectBedbankRoomPackage,
        packageOption,
        rate,
        room,
      })
    }
    showSnackbar(`${capitalise(offer.saleUnit)} ${roomIndex + 1} has been updated`, 'success', { action: { label: 'OK' } })
    close()
  }, [dispatch, offer.saleUnit, room, roomIndex, close])

  useEffect(() => {
    if (roomIndex === -1) {
      close()
    }
  }, [close, roomIndex])

  const isDesktopLayout = useScreenSizeUp('tablet')

  const isHorizontalSliderEnabled = !isDesktopLayout

  if (roomIndex === -1) {
    return null
  }

  return (
    <ModalBase height="full" size="L">
      <ModalHeader
        title={`${capitalise(offer.saleUnit)} ${roomIndex + 1} of ${rooms.length}`}
        subtitle={formatOccupantsShort([room])}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <Pane background="neutral-six" tabletBackground="none" padding="8 0 0 0" tabletPadding={12} desktopPadding={20}>
          <Group direction="vertical" gap={8} tabletGap={20}>
            {displayablePackages.map(packageView => <BedbankIndividualRoomSelectRoom
              key={packageView.package.roomId}
              packageView={packageView}
              currentRoom={room}
              optionVariant={isHorizontalSliderEnabled ? 'carousel' : 'stacked'}
              onOptionSelect={onOptionSelect}
              remainingAvailableRoomsBySupplierCode={remainingAvailabilityByPackageAndSupplier[packageView.package.roomId] ?? EmptyObject}
              remainingOverallAvailability={remainingOverallAvailabilityByPackage[packageView.package.roomId] ?? 0}
            />)}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'remainingAvailabilityByPackageAndSupplier'
  | 'remainingOverallAvailabilityByPackage'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.bedbankOffer!,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  rooms: state.rooms,
  remainingAvailabilityByPackageAndSupplier: getRemainingAvailabilityByPackageAndSupplier(state, appState),
  remainingOverallAvailabilityByPackage: getRemainingOverallAvailabilityByPackage(state, appState),
}), BedbankIndividualRoomPackageSelectionModal)
